import { Form, Input, Modal, Select, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { WAIVE_MODE_LABEL, labelOf } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { PenaltyType } from 'pages/star-shared/types';
import { extractArray, formatNumber } from 'pages/star-shared/utils';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValue {
  user?: UserPickerValue;
  typeId?: number;
  note?: string;
}

// Ghi phạt: POST /stars/admin/penalties { userId, typeId, note? }.
const RecordPenaltyModal = ({ open, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm<FormValue>();
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState<PenaltyType[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    form.setFieldsValue({ user: undefined, typeId: undefined, note: '' });
    let cancelled = false;
    const fetchTypes = async () => {
      setTypesLoading(true);
      try {
        const res = await AxiosUtils.get(API.starsPenaltyTypes);
        if (!cancelled) setTypes(extractArray<PenaltyType>(res).filter((t) => t.active !== false));
      } catch (err) {
        if (!cancelled) notification.error({ message: mapStarError(err) });
      } finally {
        if (!cancelled) setTypesLoading(false);
      }
    };
    fetchTypes();
    return () => {
      cancelled = true;
    };
  }, [open, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValue) => {
    if (!values.user?.value || !values.typeId) return;
    setLoading(true);
    try {
      await AxiosUtils.post(API.starsPenalties, {
        userId: values.user.value,
        typeId: values.typeId,
        note: values.note?.trim() || undefined,
      });
      notification.success({ message: 'Đã ghi phạt.' });
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Ghi phạt" open={open} onCancel={handleClose} footer={null} destroyOnClose maskClosable={false} width={520}>
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item
          label="Người bị phạt"
          name="user"
          rules={[{ required: true, message: 'Vui lòng chọn người.' }]}
          extra="Không ghi phạt cho chính mình."
        >
          <UserPicker />
        </Form.Item>
        <Form.Item label="Loại phạt" name="typeId" rules={[{ required: true, message: 'Vui lòng chọn loại phạt.' }]}>
          <Select
            loading={typesLoading}
            placeholder="Chọn loại phạt"
            showSearch
            optionFilterProp="label"
            options={types.map((t) => ({
              value: t.id,
              label: `${t.name} — ${formatNumber(t.starCost)} ⭐ · ${labelOf(WAIVE_MODE_LABEL, t.waiveMode)}`,
            }))}
            notFoundContent={typesLoading ? undefined : 'Chưa có loại phạt đang dùng.'}
          />
        </Form.Item>
        <Form.Item label="Ghi chú" name="note" rules={[{ max: 255, message: 'Tối đa 255 ký tự.' }]}>
          <Input.TextArea rows={3} maxLength={255} showCount placeholder="Sự việc, ngày giờ" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton text="Ghi phạt" bgColor="primary-red" htmlType="submit" loading={loading} />
        </div>
      </Form>
    </Modal>
  );
};

export default RecordPenaltyModal;
